import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function SidebarMenuItem({ item, isActive, onSelect, selectedTab, setSelectedTab }) {

    const navigate = useNavigate();
    const location = useLocation();

    const [isExpanded, setIsExpanded] = useState(false);
    const [height, setHeight] = useState(0);
    const contentRef = useRef(null);

    // Check if current path matches this item
    const isCurrentPath = () => {
        if (item.url) {
            return location.pathname === item.url;
        }
        return false;
    };

    // Check if any sub-item matches current path
    const hasActiveSubItem = () => {
        if (!item.subItems) return false;
        return item.subItems.some(subItem => {
            const fullPath = (item.url || '') + subItem.url;
            return location.pathname === fullPath;
        });
    };

    const isItemActive = isCurrentPath() || hasActiveSubItem();

    // Debug logging
    useEffect(() => {
        if (item.id === 'dashboard') {
            console.log('Dashboard Debug:', {
                itemUrl: item.url,
                currentPath: location.pathname,
                isCurrentPath: isCurrentPath(),
                isItemActive
            });
        }
    }, [location.pathname]);

    // Update height when expanded state changes
    useEffect(() => {
        if (contentRef.current) {
            if (isExpanded) {
                setHeight(contentRef.current.scrollHeight);
            } else {
                setHeight(0);
            }
        }
    }, [isExpanded]);

    // Auto-expand if has active sub-item
    useEffect(() => {
        if (hasActiveSubItem()) {
            setIsExpanded(true);
        }
    }, [location.pathname]);

    const handleClick = () => {
        if(!item.subItems) {
            setSelectedTab(item.id);
            navigate(item.url);
            return;
        }
        onSelect(item.id);
        setIsExpanded(!isExpanded);
    };

    const handleSubItemClick = (subItem) => {
        const itemUrl = item.url || '';
        setSelectedTab(subItem.id);
        navigate(itemUrl + subItem.url);
    };

    const isSubItemActive = (subItem) => {
        const fullPath = (item.url || '') + subItem.url;
        return location.pathname === fullPath;
    };

    return (
        <div>
            <div
                className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${isItemActive ? 'bg-green-50 text-green-600 border-l-4 border-green-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={handleClick}
            >
                <div className="flex items-center space-x-3">
                    {item.icon && <item.icon size={16} />}
                    <span className="font-medium text-base">{item.title}</span>
                </div>
                {item.subItems && (
                    isExpanded ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )
                )}
            </div>

            {item.subItems && isExpanded && (
                <div
                    ref={contentRef}
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{ height: `${height}px` }}
                >
                    <div className="ml-9 mt-1 space-y-1">
                        {item.subItems.map((subItem, index) => (
                            <div
                                key={index}
                                onClick={() => handleSubItemClick(subItem)}
                                className={`block px-3 py-2 text-sm rounded-md transition-colors cursor-pointer ${isSubItemActive(subItem) ? 'bg-green-100 text-green-700 font-medium' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                            >
                                {subItem.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
